import React, { useCallback } from 'react';
import {
  Card,
  Typography,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { FilterItem } from '@/types/api-definitions';

interface FilterCardProps {
  title: string;
  items: FilterItem[];
  selectedItems: string[];
  onItemChange: (item: string, checked: boolean) => void;
  defaultExpanded?: boolean;
}

const FilterCard: React.FC<FilterCardProps> = ({
  title,
  items,
  selectedItems,
  onItemChange,
  defaultExpanded = true,
}) => {
  const theme = useTheme();

  const handleCheckboxChange = useCallback(
    (slug: string) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        onItemChange(slug, e.target.checked);
      };
    },
    [onItemChange],
  );

  return (
    <Card
      sx={{
        mb: 2,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: theme.palette.ui.background,
      }}
    >
      <Accordion
        defaultExpanded={defaultExpanded}
        sx={{
          backgroundColor: theme.palette.ui.background,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: theme.customFontWeights.semibold,
              color: theme.palette.brand.primary,
            }}
          >
            {title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              borderRadius: '16px',
              backgroundColor: theme.palette.searchFilter.filterOptionBackground,
              padding: 1,
            }}
          >
            {items.map((item) => (
              <FormControlLabel
                key={item.slug}
                control={
                  <Checkbox
                    size='small'
                    checked={selectedItems.includes(item.slug)}
                    onChange={handleCheckboxChange(item.slug)}
                  />
                }
                label={
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.searchFilter.filterOptionText }}
                  >
                    {item.name} ({item.count})
                  </Typography>
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

export default FilterCard;
